import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Observable, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SearchResultTable } from '../search-result-table/search-result-table';
import { ZnAlert } from '../zn-alert/zn-alert';
import { ZnButton } from '../zn-button/zn-button';
import { IModalContent } from '../zn-modal-wrapper/interfaces/IModalContent';
import {
  ExcelImportErrorLike,
  ExcelImportErrorsExportLike,
} from '../interfaces/dtos/excel-import.models';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { localizeWithFallback } from '../utils/localization-utils';

@Component({
  selector: 'zn-excel-import-errors-modal',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    ReactiveFormsModule,
    SearchResultTable,
    ZnAlert,
    ZnButton,
  ],
  templateUrl: './zn-excel-import-errors-modal.html',
  styleUrl: './zn-excel-import-errors-modal.scss',
})
export class ZnExcelImportErrorsModal implements IModalContent, OnInit {
  fileName = input<string>('');
  errors = input<ExcelImportErrorLike[]>([]);
  title = input<string>('');
  subTitle = input<string>('');
  exportErrors = input<((input: ExcelImportErrorsExportLike) => Observable<Blob>) | undefined>();

  protected readonly activeModal = inject(NgbActiveModal);
  protected readonly localizationService = inject(LocalizationService);
  protected readonly localizationResource = ZendraLocalizationServices;

  searchControl = new FormControl('', { nonNullable: true });
  exportError = signal<string | null>(null);
  exportLoading = signal(false);
  loading = computed(() => this.exportLoading());

  filteredErrors = signal<ExcelImportErrorLike[]>([]);

  readonly errorCount = computed(() => this.errors().length);

  readonly tableColumns = [
    {
      id: 'itemNumber',
      name: this.localize('ZnExcelImportErrorsModalItemNro', 'Nro.'),
      field: 'itemNumber',
      minWidth: 60,
      maxWidth: 80,
    },
    {
      id: 'rowNumber',
      name: this.localize('ZnExcelImportErrorsModalRowNro', 'Linea'),
      field: 'rowNumber',
      minWidth: 60,
      maxWidth: 90,
    },
    {
      id: 'error',
      name: this.localize('ZnExcelImportErrorsModalError', 'Error'),
      field: 'message',
      minWidth: 520,
      cssClass: 'zn-wrap-cell',
      formatter: (_row: number, _cell: number, value = '') => {
        const escapedMessage = this.escapeHtml(value);
        return `<span title="${escapedMessage}">${escapedMessage}</span>`;
      },
    },
  ];

  readonly tableOptions = {
    enableHeaderMenu: false,
    enableHeaderButton: false,
    enableGridMenu: false,
    enableColumnPicker: false,
    enableSorting: false,
    autoHeight: false,
    rowHeight: 64,
    gridHeight: 320,
  };

  readonly tableDataset = computed(() =>
    this.filteredErrors().map((error, index) => ({
      id: index,
      itemNumber: index + 1,
      rowNumber: error.rowNumber,
      message: error.message ?? '',
    }))
  );

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(value => this.applyFilter(value));

    this.filteredErrors.set([]);

    effect(() => {
      this.errors();
      this.applyFilter(this.searchControl.getRawValue());
    });
  }
  readonly canSave = signal(true);

  ngOnInit(): void {
    this.filteredErrors.set([...this.errors()]);
  }

  confirm(): void {
    this.activeModal.close({ retry: true });
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  onExport(): void {
    const exportErrors = this.exportErrors();
    if (!exportErrors || this.exportLoading()) {
      return;
    }

    this.exportError.set(null);
    this.exportLoading.set(true);

    exportErrors({
      fileName: this.fileName(),
      errors: this.errors(),
    }).subscribe({
      next: blob => {
        this.downloadFile(blob, this.fileName() || 'ImportErrors.xlsx');
        this.exportLoading.set(false);
      },
      error: err => {
        this.exportError.set(this.getErrorMessage(err));
        this.exportLoading.set(false);
      },
    });
  }

  private applyFilter(query: string): void {
    const normalized = this.normalize(query);
    if (!normalized) {
      this.filteredErrors.set([...this.errors()]);
      return;
    }

    this.filteredErrors.set(
      this.errors().filter(error => {
        const haystack = [
          error.rowNumber?.toString() ?? '',
          error.columnName ?? '',
          error.message ?? '',
        ]
          .map(value => this.normalize(value))
          .join(' ');

        return haystack.includes(normalized);
      })
    );
  }

  private normalize(value: string): string {
    return value
      .normalize('NFD')
      .replaceAll(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim();
  }

  private downloadFile(data: Blob, fileName: string): void {
    const anchor = document.createElement('a');
    const url = globalThis.URL.createObjectURL(data);
    anchor.href = url;
    anchor.download = fileName || 'ImportErrors.xlsx';
    anchor.click();
    globalThis.URL.revokeObjectURL(url);
  }

  private getErrorMessage(err: unknown): string {
    const error = err as {
      error?: { error?: { message?: string }; message?: string };
      message?: string;
    };

    return error?.error?.error?.message
      ?? error?.error?.message
      ?? error?.message
      ?? this.localize(
        'ZnExcelImportErrorsModalExportError',
        'An error occurred while exporting the file.'
      );
  }

  private localize(key: string, fallback: string): string {
    const localizationKey = `${this.localizationResource.LanguageService}::${key}`;
    const localized = localizeWithFallback(this.localizationService, localizationKey);
    return localized === localizationKey || localized === key ? fallback : localized;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }
}
